import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { apiService } from "../../services/apiService";
import { API_ENDPOINTS } from "../../services/apiEndpoints";
import { toast } from "react-toastify";

const Subscription = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [paymentStatus, setPaymentStatus] = useState(null);

  const fetchSubscriptionPlans = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiService.get(API_ENDPOINTS.SUBSCRIPTIONS.LIST);
      let plansData = [];
      if (Array.isArray(response)) {
        plansData = response;
      } else if (response?.data) {
        if (Array.isArray(response.data)) {
          plansData = response.data;
        } else if (Array.isArray(response.data.plans)) {
          plansData = response.data.plans;
        }
      }
      if (plansData.length > 0) {
        setPlans(plansData);
      } else {
        setPlans([]);
        setError("No subscription plans available.");
      }
    } catch (err) {
      console.error("Error fetching subscription plans:", err);
      setError("Failed to load subscription plans. Please try again later.");
      setPlans([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribe = async (plan, user) => {
    try {
      setLoading(true);
      setError(null);
      const paymentPayload = { plan_id: plan.id };
      const response = await apiService.post(
        API_ENDPOINTS.SUBSCRIPTIONS.PAYMENT_INITIATE,
        paymentPayload
      );
      const orderId = response.razorpay_order_id;
      const razorpayKey = response.razorpay_key;
      if (response.amount === 0) {
        verifyPayment(
          response?.razorpay_order_id,
          "free_payment",
          "free_signature"
        );
      } else {
        payWithRazorpay(orderId, razorpayKey);
      }
    } catch (err) {
      console.error("Error initiating payment:", err);
      if (err.response && err.response.data) {
        if (err.response.data.detail) {
          setError(err.response.data.detail);
        } else if (err.response.data.error) {
          setError(err.response.data.error);
        } else if (
          err.response.data.non_field_errors &&
          err.response.data.non_field_errors.length > 0
        ) {
          setError(err.response.data.non_field_errors[0]);
        } else {
          setError("Failed to initiate payment. Please try again.");
        }
      } else if (err.response && err.response.status === 500) {
        setError(
          "Server error occurred. Please try again later or contact support if the issue persists."
        );
      } else {
        setError("Failed to initiate payment. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const payWithRazorpay = (orderId, razorpayKey) => {
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.async = true;
    script.onload = () => {
      const options = {
        key: razorpayKey,
        order_id: orderId,
        prefill: {
          name: "",
          email: "",
          contact: "",
        },
        handler: function (res) {
          if (res.razorpay_payment_id) {
            verifyPayment(
              res.razorpay_order_id,
              res.razorpay_payment_id,
              res.razorpay_signature
            );
          } else {
            setLoading(false);
          }
        },
        modal: {
          ondismiss: () => {
            console.log("Payment modal closed");
            setLoading(false);
          },
        },
      };
      const rzp = new window.Razorpay(options);
      rzp.open();
    };

    document.body.appendChild(script);
  };

  const verifyPayment = async (
    razorpayOrderId,
    razorpayPaymentId,
    razorpaySignature
  ) => {
    try {
      setLoading(true);
      setError(null);
      const verificationPayload = {
        razorpay_order_id: razorpayOrderId,
        razorpay_payment_id: razorpayPaymentId,
        razorpay_signature: razorpaySignature,
      };
      const response = await apiService.post(
        API_ENDPOINTS.SUBSCRIPTIONS.PAYMENT_VERIFY,
        verificationPayload
      );
      if (response.detail) {
        toast.success(response.detail);
        navigate("/subscription/success");
      } else {
        setPaymentStatus("1"); // Set payment status to show payment failed message
      }
    } catch (err) {
      setPaymentStatus("1"); // Set payment status to show payment failed message
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSubscriptionPlans();
    const paymentParam = searchParams.get("payment");
    if (paymentParam !== null) {
      setPaymentStatus(paymentParam);
    }
  }, [searchParams]);

  if (loading) {
    return (
      <div className="content container-fluid">
        <div className="row">
          <div className="col-12">
            <div className="card">
              <div className="card-body text-center">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <p className="mt-3">Loading subscription plans...</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="content container-fluid">
        <div className="row">
          <div className="col-12">
            <div className="card">
              <div className="card-body text-center">

                {/* Contact Support Section */}
                <div className="border-top pt-4">
                  <h5 className="card-title">
                    <i className="fe fe-headphones me-2"></i>
                    Contact Support
                  </h5>
                  <p className="card-text">
                   There is an issue with the payment. Please contact the administrator. Your account will be activated shortly.
                  </p>
                  <div className="row">
                    <div className="col-md-4">
                      <div className="contact-item">
                        <i className="fe fe-mail text-primary mb-2"></i>
                        <h6>Email Support</h6>
                        <p className="text-muted">info@morvibazaar.com</p>
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="contact-item">
                        <i className="fe fe-phone text-primary mb-2"></i>
                        <h6>Phone Support</h6>
                        <p className="text-muted">+91 76000 90040</p>
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="contact-item">
                        <i className="fe fe-clock text-primary mb-2"></i>
                        <h6>Support Hours</h6>
                        <p className="text-muted">Mon-Fri: 9AM-6PM</p>
                      </div>
                    </div>
                  </div>
                  <a href="tel:+917600090040" className="btn btn-outline-primary mt-3">
                    <i className="fe fe-phone me-2"></i>
                    Call Support
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      {(paymentStatus === "0" || paymentStatus === null) && (
        <>
          <div className="page-header">
            <div className="row align-items-center">
              <div className="col mb-3">
                <h3 className="page-title">Subscription Plans</h3>
              </div>
            </div>
          </div>
          <div className="row">
            {(plans || []).length > 0 ? (
              (plans || []).map((plan) => (
                <div key={plan.id} className="col-lg-4 col-md-6">
                  <div
                    className={`card pricing-card ${plan.popular ? "popular" : ""
                      }`}
                  >
                    {plan.popular && (
                      <div className="popular-badge">
                        <span>Most Popular</span>
                      </div>
                    )}
                    <div className="card-body text-center">
                      <div className="plan-header">
                        <h4 className="plan-name">{plan.name}</h4>
                        <div className="plan-price">
                          <span className="currency">{plan.currency}</span>
                          <span className="amount">{plan.price}</span>
                        </div>
                        <div className="plan-duration mb-3" style={{ fontSize: '15px' }}>
                          {plan.duration_months} Month
                        </div>
                      </div>
                      <div className="plan-features">
                        <ul className="feature-list">
                          {(plan.features || []).map((feature, index) => (
                            <li key={index} className="feature-item">
                              <i className="fe fe-check text-success me-2"></i>
                              {feature}
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div className="plan-action">
                        <button
                          className={`btn ${plan.popular ? "btn-primary" : "btn-outline-primary"
                            } btn-lg w-100`}
                          onClick={() => handleSubscribe(plan, null)}
                        >
                          {plan.popular ? "Get Started" : "Choose Plan"}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="col-12">
                <div className="card">
                  <div className="card-body text-center">
                    <div className="alert alert-info">
                      <i className="fe fe-info me-2"></i>
                      No subscription plans are currently available. Please
                      check back later.
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </>
      )}
      {paymentStatus === "1" && (
        <div className="content container-fluid">
          <div className="row">
            <div className="col-12">
              <div className="card">
                <div className="card-body text-center">
                  {/* Contact Support Section */}
                  <div className="pt-4">
                    <h5 className="card-title">
                      <i className="fe fe-headphones me-2"></i>
                      Contact Support
                    </h5>
                    <p className="card-text">
                      There is an issue with the payment. Please contact the administrator. Your account will be activated shortly.
                    </p>
                    <div className="row">
                      <div className="col-md-4">
                        <div className="contact-item">
                          <i className="fe fe-mail text-primary mb-2"></i>
                          <h6>Email Support</h6>
                          <p className="text-muted">info@morvibazaar.com</p>
                        </div>
                      </div>
                      <div className="col-md-4">
                        <div className="contact-item">
                          <i className="fe fe-phone text-primary mb-2"></i>
                          <h6>Phone Support</h6>
                          <p className="text-muted">+91 76000 90040</p>
                        </div>
                      </div>
                      <div className="col-md-4">
                        <div className="contact-item">
                          <i className="fe fe-clock text-primary mb-2"></i>
                          <h6>Support Hours</h6>
                          <p className="text-muted">Mon-Fri: 9AM-6PM</p>
                        </div>
                      </div>
                    </div>
                    <a href="tel:+917600090040" className="btn btn-outline-primary mt-3">
                      <i className="fe fe-phone me-2"></i>
                      Call Support
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Subscription;
